/**
 * Widget Carte - Affiche les offres touristiques sur une carte interactive avec Leaflet
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseWidget } from "../components/BaseWidget";
import "../components/FilterPanel";
import "../components/WidgetHeader";
import "../components/WidgetLayout";
import "../components/Icon";
import { ObjetTouristique } from "../types/apidae.d";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import leafletCSS from "leaflet/dist/leaflet.css?inline";
import mapWidgetStyles from "../styles/map-widget.css?inline";

// Importer les icônes Leaflet
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { getImageUrl, getLabel, t } from "../utils/widgetsUtils";
import toGeoJSON from "@mapbox/togeojson";
import JSZip from "jszip";

// Configurer l'icône par défaut de Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

@customElement("apidae-widget-carte")
export class MapWidget extends BaseWidget {
  static styles: CSSResultGroup = [
    unsafeCSS(leafletCSS),
    BaseWidget.sharedStyles,
    unsafeCSS(mapWidgetStyles),
  ];

  @state()
  private map: L.Map | null = null;

  @state()
  private markers: L.Marker[] = [];

  // MarkerClusterGroup pour gérer les clusters
  private markerClusterGroup:
    | (L.MarkerClusterGroup & { iconCreateFunction?: any })
    | null = null;

  // Map pour associer les items aux marqueurs
  private itemMarkerMap: Map<ObjetTouristique, L.Marker> = new Map();

  private mapId: string = "";

  // Timer pour le debounce des requêtes API
  private loadDataDebounceTimer: number | null = null;

  // Flag pour indiquer si la carte est initialisée (pour éviter les rechargements lors de l'init)
  private mapInitialized: boolean = false;

  // Observer pour détecter quand la carte devient visible
  private visibilityObserver?: IntersectionObserver;

  // État pour afficher le bouton "Rechercher dans cette zone"
  @state()
  private showSearchButton: boolean = false;

  // Paramètres de la zone à rechercher (stockés quand la carte bouge)
  private pendingSearchParams: {
    center: { type: "Point"; coordinates: [number, number] };
    radius: number;
    communeCodesInsee?: string[];
  } | null = null;

  // Handler (phase capture) pour empêcher unspiderfy au clic sur la carte quand un spider est affiché
  private _mapClickCaptureHandler: ((e: MouseEvent) => void) | null = null;
  private _mapMouseDownCaptureHandler: ((e: MouseEvent) => void) | null = null;
  /** Position du mousedown pour distinguer un vrai clic d’un pan (déplacement de la carte). */
  private _mapMouseDownAt: { x: number; y: number } | null = null;
  /** True pendant updateMarkers() pour ne pas dispatcher item-deselect au closePopup (ex: scroll infini). */
  private _isUpdatingMarkers: boolean = false;
  /** IDs des items dont l'image du popup a échoué au chargement (ne pas afficher l'image). */
  private failedPopupImages: Set<number> = new Set();

  /** URL d'un fichier KML, KMZ ou GPX à afficher comme tracé (ex. plan dans la fiche détail). */
  @property({ type: String, attribute: "track-url" })
  trackUrl: string | undefined;

  connectedCallback() {
    super.connectedCallback();
    this.mapId = `map-${Math.random().toString(36).substring(2, 11)}`;
    this.setupVisibilityObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupVisibilityObserver();
    this.cleanupMap();
  }

  /**
   * Configure un observer pour détecter quand la carte devient visible
   */
  private setupVisibilityObserver(): void {
    // Attendre que le DOM soit prêt
    requestAnimationFrame(() => {
      const container = this.shadowRoot?.getElementById(this.mapId);
      if (!container) {
        // Réessayer après un court délai
        setTimeout(() => this.setupVisibilityObserver(), 100);
        return;
      }

      // Créer un IntersectionObserver pour détecter quand la carte devient visible
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
              // La carte est maintenant visible - forcer une vérification et réinitialisation si nécessaire
              this.checkAndReinitializeIfNeeded();
            }
          });
        },
        {
          threshold: 0.1, // Déclencher quand au moins 10% de la carte est visible
        },
      );

      this.visibilityObserver.observe(container);
    });
  }

  /**
   * Vérifie si la carte doit être réinitialisée et le fait si nécessaire
   * Utile quand la carte devient visible après avoir été cachée
   */
  public checkAndReinitializeIfNeeded(): void {
    const container = this.shadowRoot?.getElementById(this.mapId);
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();

    // Si la carte est initialisée mais n'a pas de taille valide, la réinitialiser
    if (this.mapInitialized && this.map) {
      if (rect.width === 0 || rect.height === 0) {
        // La carte était repliée, la réinitialiser complètement
        this.mapInitialized = false;
        this.cleanupMap();
        requestAnimationFrame(() => {
          setTimeout(() => this.initMapWhenReady(), 100);
        });
      } else {
        // Invalider la taille pour s'assurer que la carte est correctement affichée
        requestAnimationFrame(() => {
          if (this.map) {
            this.map.invalidateSize();
            requestAnimationFrame(() => {
              if (this.map) {
                this.map.invalidateSize();
              }
            });
          }
        });
      }
    } else if (
      !this.mapInitialized &&
      this.config &&
      rect.width > 0 &&
      rect.height > 0
    ) {
      // La carte n'est pas initialisée mais le conteneur est visible, l'initialiser
      requestAnimationFrame(() => {
        setTimeout(() => this.initMapWhenReady(), 100);
      });
    }
  }

  /**
   * Nettoie l'observer de visibilité
   */
  private cleanupVisibilityObserver(): void {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = undefined;
    }
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    // Mettre à jour la sélection des marqueurs quand selectedItemId change
    if (changedProperties.has("selectedItemId")) {
      this.updateMarkerSelection();
    }

    if (changedProperties.has("items")) {
      // Toujours initialiser/mettre à jour la carte, même s'il n'y a pas d'items ou pas de coordonnées GPS
      // La carte doit toujours s'afficher, avec ou sans pins
      if (this.items.length > 0) {
        requestAnimationFrame(() => {
          setTimeout(() => this.initMapWhenReady(), 100);
        });
      } else if (this.mapInitialized && this.map) {
        // Si la carte est déjà initialisée et qu'on a plus de résultats,
        // vérifier d'abord si la carte est visible (pour gérer le cas où elle était repliée)
        requestAnimationFrame(() => {
          const container = this.shadowRoot?.getElementById(this.mapId);
          if (container) {
            const rect = container.getBoundingClientRect();
            // Si le conteneur n'a pas de taille, la carte était probablement repliée
            // Il faut la réinitialiser complètement
            if (rect.width === 0 || rect.height === 0) {
              // Réinitialiser complètement la carte
              this.mapInitialized = false;
              this.cleanupMap();
              // Réessayer après un délai pour que le conteneur ait une taille
              setTimeout(() => this.initMapWhenReady(), 200);
            } else {
              // Sinon, juste mettre à jour les marqueurs pour retirer tous les pins (carte sans pin)
              setTimeout(() => {
                this.updateMarkers();
                // Invalider la taille pour s'assurer que la carte est correctement affichée
                if (this.map) {
                  this.map.invalidateSize();
                }
              }, 100);
            }
          } else {
            // Conteneur non trouvé, réinitialiser
            this.mapInitialized = false;
            this.cleanupMap();
            setTimeout(() => this.initMapWhenReady(), 200);
          }
        });
      } else if (!this.mapInitialized && this.config) {
        // Si la carte n'est pas encore initialisée mais qu'on a une config,
        // l'initialiser même sans items (carte vide)
        requestAnimationFrame(() => {
          setTimeout(() => this.initMapWhenReady(), 100);
        });
      }
    }

    // Si la visibilité des filtres change, recalculer la taille de la carte
    if (changedProperties.has("filtersVisible") && this.map) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize();
            // Invalider une deuxième fois pour s'assurer que la taille est correcte
            requestAnimationFrame(() => {
              if (this.map) {
                this.map.invalidateSize();
              }
            });
          }
        }, 150);
      });
    }
  }

  /**
   * Charge un fichier KML, KMZ ou GPX depuis trackUrl et l'affiche sur la carte.
   * Appelé uniquement quand trackUrl est défini (mode tracé dans la fiche détail).
   */
  private loadTrackLayerAndFinishInit(): void {
    const url = this.trackUrl;
    if (!url || !this.map) return;

    const path = url.split(/[#?]/)[0].toLowerCase();
    const isKmz = path.endsWith(".kmz");
    const isGpx = path.endsWith(".gpx");

    const primaryColor =
      this.config?.visualCustomization?.colors?.primary || "#168ac8";

    const addLayerAndFinish = (geojson: GeoJSON.GeoJSON) => {
      if (!this.map) return;
      const layer = L.geoJSON(geojson, {
        style: {
          color: primaryColor,
          weight: 4,
          opacity: 0.8,
        },
        pointToLayer: (_feature, latlng) => {
          return L.marker(latlng, {
            icon: this.createCustomMarkerIcon(primaryColor),
          });
        },
      });
      layer.addTo(this.map);
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 17 });
      } else {
        this.map.setView([46.6034, 1.8883], 6);
      }
      requestAnimationFrame(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.mapInitialized = true;
          this.requestUpdate();
        }
      });
    };

    const parseAndAdd = (xmlText: string, type: "kml" | "gpx") => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, "text/xml");
      const geojson = type === "kml" ? toGeoJSON.kml(doc) : toGeoJSON.gpx(doc);
      addLayerAndFinish(geojson);
    };

    if (isKmz) {
      fetch(url)
        .then((r) => r.blob())
        .then((blob) => JSZip.loadAsync(blob))
        .then((zip) => {
          const kmlEntry = Object.values(zip.files).find(
            (f) => !f.dir && f.name.toLowerCase().endsWith(".kml"),
          );
          if (!kmlEntry) throw new Error("Aucun fichier KML dans le KMZ");
          return kmlEntry.async("text");
        })
        .then((kmlText) => parseAndAdd(kmlText, "kml"))
        .catch((err) => {
          console.error("Erreur chargement tracé KMZ/KML:", err);
          if (this.map) {
            this.map.setView([46.6034, 1.8883], 6);
            this.mapInitialized = true;
            this.requestUpdate();
          }
        });
      return;
    }

    fetch(url)
      .then((r) => r.text())
      .then((text) => parseAndAdd(text, isGpx ? "gpx" : "kml"))
      .catch((err) => {
        console.error("Erreur chargement tracé KML/GPX:", err);
        if (this.map) {
          this.map.setView([46.6034, 1.8883], 6);
          this.mapInitialized = true;
          this.requestUpdate();
        }
      });
  }

  private cleanupMap() {
    // Annuler le timer de debounce s'il existe
    if (this.loadDataDebounceTimer !== null) {
      window.clearTimeout(this.loadDataDebounceTimer);
      this.loadDataDebounceTimer = null;
    }

    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
      if (this.map) {
        this.map.removeLayer(this.markerClusterGroup);
      }
      this.markerClusterGroup = null;
    }
    if (this.map) {
      const container = this.map.getContainer();
      if (this._mapClickCaptureHandler) {
        container.removeEventListener(
          "click",
          this._mapClickCaptureHandler,
          true,
        );
        this._mapClickCaptureHandler = null;
      }
      if (this._mapMouseDownCaptureHandler) {
        container.removeEventListener(
          "mousedown",
          this._mapMouseDownCaptureHandler,
          true,
        );
        this._mapMouseDownCaptureHandler = null;
      }
      this._mapMouseDownAt = null;
      this.map.remove();
      this.map = null;
    }
    this.markers = [];
    this.itemMarkerMap.clear();
    this.mapInitialized = false;
  }

  /**
   * Vérifie si une zone (center/radius) est comprise dans le périmètre de configuration
   * Retourne un objet avec isValid (si on peut faire l'appel) et communeCodesInsee (si applicable)
   */
  private isZoneWithinConfigPerimeter(
    center: L.LatLng,
    radius: number,
  ): { isValid: boolean; communeCodesInsee?: string[] } {
    const geoPeri = this.config?.contentCustomization.geographicPerimeter;

    // Si pas de périmètre configuré, on peut toujours faire l'appel avec center/radius
    if (!geoPeri) {
      return { isValid: true };
    }

    // Si le périmètre est défini par des communes, on peut faire l'appel
    // avec center/radius/communeCodesInsee (les trois en même temps)
    if (geoPeri.type === "communes") {
      return { isValid: true, communeCodesInsee: geoPeri.communeIds };
    }

    // Si le périmètre est défini par radius, vérifier que la nouvelle zone est incluse
    if (geoPeri.type === "radius") {
      const configCenter = L.latLng(geoPeri.latitude, geoPeri.longitude);
      const configRadius = geoPeri.radius;

      // Calculer la distance entre les deux centres
      const distanceBetweenCenters = configCenter.distanceTo(center);

      // La nouvelle zone est incluse si :
      // distance entre les centres + nouveau rayon <= ancien rayon
      // Autrement dit : le nouveau cercle doit être complètement dans l'ancien
      const isValid = distanceBetweenCenters + radius <= configRadius;

      return { isValid, communeCodesInsee: undefined };
    }

    // Par défaut, ne pas faire l'appel
    return { isValid: false };
  }

  /**
   * Prépare les paramètres de recherche et affiche le bouton "Rechercher dans cette zone"
   */
  private prepareSearchButton(): void {
    if (!this.map || !this.config) return;

    // Si pas de widgetId, ne pas afficher le bouton (données gérées par le parent)
    // En mode composition (disableDetailView), on affiche quand même le bouton car
    // l'utilisateur peut vouloir rechercher dans une zone spécifique
    if (!this.widgetId) {
      return;
    }

    try {
      // Obtenir les bounds de la carte visible
      const bounds = this.map.getBounds();
      const center = bounds.getCenter();

      // Calculer les distances du centre aux 4 directions (bords du rectangle)
      const north = L.latLng(bounds.getNorth(), center.lng);
      const south = L.latLng(bounds.getSouth(), center.lng);
      const east = L.latLng(center.lat, bounds.getEast());
      const west = L.latLng(center.lat, bounds.getWest());

      // Calculer les distances du centre aux 4 bords
      const distToNorth = center.distanceTo(north);
      const distToSouth = center.distanceTo(south);
      const distToEast = center.distanceTo(east);
      const distToWest = center.distanceTo(west);

      // Prendre la distance maximale pour couvrir toute la zone visible
      const radius = Math.max(distToNorth, distToSouth, distToEast, distToWest);

      // Vérifier si la zone est dans le périmètre de configuration
      const { isValid, communeCodesInsee } = this.isZoneWithinConfigPerimeter(
        center,
        radius,
      );

      // Si la zone n'est pas valide, ne pas afficher le bouton
      if (!isValid) {
        this.showSearchButton = false;
        this.pendingSearchParams = null;
        this.requestUpdate();
        return;
      }

      // Créer le GeoJson pour le centre (format: [longitude, latitude])
      const centerGeoJson: { type: "Point"; coordinates: [number, number] } = {
        type: "Point",
        coordinates: [center.lng, center.lat],
      };

      // Stocker les paramètres de recherche
      this.pendingSearchParams = {
        center: centerGeoJson,
        radius,
        communeCodesInsee,
      };

      // Afficher le bouton
      this.showSearchButton = true;
      this.requestUpdate();
    } catch (error) {
      console.error("Erreur lors du calcul des bounds de la carte:", error);
      this.showSearchButton = false;
      this.pendingSearchParams = null;
    }
  }

  /**
   * Calcule le centre et le rayon de la zone visible et recharge les données
   * Appelé quand l'utilisateur clique sur le bouton "Rechercher dans cette zone"
   */
  private loadDataFromMapBounds(): void {
    if (this.isChildWidget) {
      this.dispatchEvent(
        new CustomEvent("child-context-change", {
          detail: {
            source: "map",
            center: this.pendingSearchParams?.center,
            radius: this.pendingSearchParams?.radius,
            communeCodesInsee: this.pendingSearchParams?.communeCodesInsee,
          },
          bubbles: true,
          composed: true,
        }),
      );

      return;
    }

    if (!this.pendingSearchParams) return;

    // Recharger les données avec les paramètres stockés
    this.loadData(
      1,
      false,
      this.pendingSearchParams.center,
      this.pendingSearchParams.radius,
    )
      .then(() => {
        // Masquer le bouton après le chargement
        this.showSearchButton = false;
        this.pendingSearchParams = null;
        this.requestUpdate();
      })
      .catch((error) => {
        console.error(
          "Erreur lors du rechargement des données depuis la carte:",
          error,
        );
      });
  }

  /**
   * Retourne les items ayant des coordonnées, dédupliqués par id (un même objet peut apparaître plusieurs fois en agenda).
   */
  private getUniqueItemsWithCoords(): ObjetTouristique[] {
    const seen = new Set<number>();
    return this.items.filter((item) => {
      if (!item.localisation?.geolocalisation?.geoJson?.coordinates)
        return false;
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  /**
   * Met à jour les marqueurs sur la carte existante sans réinitialiser la vue
   */
  private updateMarkers(): void {
    if (!this.map || !this.markerClusterGroup) return;

    this._isUpdatingMarkers = true;
    // Fermer tous les popups ouverts avant de mettre à jour les marqueurs (ne pas désélectionner)
    this.map.closePopup();

    // Retirer tous les marqueurs existants du cluster
    this.markerClusterGroup.clearLayers();
    this.markers = [];
    this.itemMarkerMap.clear();

    // Filtrer les items avec coordonnées valides et dédupliquer par id (ex: agenda même objet sur plusieurs dates)
    const itemsWithCoords = this.getUniqueItemsWithCoords();

    // Créer les nouveaux marqueurs
    itemsWithCoords.forEach((item) => {
      const coords = item.localisation?.geolocalisation?.geoJson?.coordinates;
      if (coords && coords.length >= 2) {
        const [lng, lat] = coords;
        const latLng: L.LatLngExpression = [lat, lng];

        // Créer le marqueur avec l'icône personnalisée
        const customIcon = this.createCustomMarkerIcon();
        const marker = L.marker(latLng, { icon: customIcon });
        this.markers.push(marker);

        // Stocker la relation item -> marker
        this.itemMarkerMap.set(item, marker);

        // Créer le contenu du popup
        const popupContent = this.createPopupContent(item);
        marker.bindPopup(popupContent, {
          maxWidth: 300,
          className: "custom-popup",
        });

        // Écouter le clic sur le marqueur pour sélectionner dans la liste (si disableDetailView)
        marker.on("click", () => {
          if (this.disableDetailView) {
            // Mettre à jour la sélection
            this.selectedItemId = item.id;
            this.updateMarkerSelection();
            // Émettre un événement pour sélectionner dans la liste
            this.dispatchEvent(
              new CustomEvent("item-click", {
                detail: { item },
                bubbles: true,
                composed: true,
              }),
            );
          }
        });

        // Écouter l'ouverture du popup pour changer la couleur du marqueur et recentrer la carte si besoin
        marker.on("popupopen", () => {
          this.updateMarkerIcon(marker, "#2F2F2F");
          this.ensureMarkerVisibleWithPopup(marker);
        });

        // Écouter la fermeture du popup pour remettre la couleur par défaut ou la couleur de sélection
        marker.on("popupclose", () => {
          // Si c'est l'item sélectionné, garder la couleur de sélection (gris foncé)
          if (this.selectedItemId === item.id) {
            this.updateMarkerIcon(marker, "#2F2F2F");
          } else {
            // Utiliser la couleur primary de la config
            const primaryColor =
              this.config?.visualCustomization?.colors?.primary || "#168ac8";
            this.updateMarkerIcon(marker, primaryColor);
          }

          // Si on est en mode composition (disableDetailView), désélectionner dans la liste aussi (sauf pendant updateMarkers ex: scroll)
          if (
            !this._isUpdatingMarkers &&
            this.disableDetailView &&
            this.selectedItemId === item.id
          ) {
            this.selectedItemId = null;
            this.dispatchEvent(
              new CustomEvent("item-deselect", {
                detail: { itemId: item.id },
                bubbles: true,
                composed: true,
              }),
            );
          }
        });

        // Si c'est l'item sélectionné, mettre en évidence le marqueur (gris foncé)
        if (this.selectedItemId === item.id) {
          this.updateMarkerIcon(marker, "#2F2F2F");
        } else {
          // Sinon, couleur primary de la config
          const primaryColor =
            this.config?.visualCustomization?.colors?.primary || "#168ac8";
          this.updateMarkerIcon(marker, primaryColor);
        }

        // Ajouter le marqueur au cluster
        this.markerClusterGroup!.addLayer(marker);
      }
    });
    this._isUpdatingMarkers = false;
  }

  /**
   * Initialise la carte quand le conteneur est prêt (a une taille)
   */
  private initMapWhenReady(attempts: number = 0): void {
    const container = this.shadowRoot?.getElementById(this.mapId);
    const maxAttempts = 10;
    if (!container) {
      if (attempts < maxAttempts) {
        // Réessayer après un court délai si le conteneur n'est pas encore disponible
        requestAnimationFrame(() => {
          setTimeout(() => this.initMapWhenReady(attempts + 1), 50);
        });
      } else {
        console.warn("Map container not found after multiple attempts");
      }
      return;
    }

    // Vérifier que le conteneur a une taille
    const rect = container.getBoundingClientRect();
    const maxAttemptsSize = 20;
    if (rect.width === 0 || rect.height === 0) {
      if (attempts < maxAttemptsSize) {
        // Réessayer si le conteneur n'a pas encore de taille (ex: section carte en bas de page)
        requestAnimationFrame(() => {
          setTimeout(() => this.initMapWhenReady(attempts + 1), 50);
        });
      } else {
        console.warn("Map container has no size after multiple attempts");
      }
      return;
    }

    // Le conteneur est prêt, initialiser la carte
    this.initializeMap();
  }

  private initializeMap() {
    const container = this.shadowRoot?.getElementById(this.mapId);
    if (!container) {
      console.warn("Map container not found");
      return;
    }

    // Si la carte existe déjà ET est attachée au même conteneur, mettre à jour seulement les marqueurs.
    // Sinon (conteneur remplacé, ex. preview back office qui re-render), réinitialiser pour attacher à l’élément actuel.
    if (
      this.map &&
      this.markerClusterGroup &&
      this.map.getContainer() === container
    ) {
      this.updateMarkers();
      return;
    }

    // Nettoyer la carte existante (ou conteneur a changé)
    this.cleanupMap();

    // Filtrer les items avec coordonnées valides et dédupliquer par id (ex: agenda même objet sur plusieurs dates)
    const itemsWithCoords = this.getUniqueItemsWithCoords();

    // Initialiser la carte même s'il n'y a pas d'items avec coordonnées
    // La carte s'affichera simplement sans pins

    try {
      // Créer la carte Leaflet
      this.map = L.map(container, {
        scrollWheelZoom: true,
        dragging: true,
        zoomControl: true,
        attributionControl: true,
      });

      // Ajouter la couche de tuiles OpenStreetMap
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(this.map);

      // Mode tracé uniquement (KML/KMZ/GPX) : charger le fichier et afficher le tracé
      if (this.trackUrl) {
        this.loadTrackLayerAndFinishInit();
        return;
      }

      // Récupérer la couleur primary pour les clusters
      const primaryColor =
        this.config?.visualCustomization?.colors?.primary || "#168ac8";

      // Créer le MarkerClusterGroup avec style personnalisé
      // maxClusterRadius: distance maximale (en pixels) pour regrouper les marqueurs
      // Plus cette valeur est petite, plus les marqueurs doivent être proches pour être regroupés
      this.markerClusterGroup = L.markerClusterGroup({
        iconCreateFunction: (cluster: any) => {
          const count = cluster.getChildCount();
          return this.createClusterIcon(count, primaryColor);
        },
        maxClusterRadius: 50, // Seulement regrouper les marqueurs très proches (50 pixels)
        zoomToBoundsOnClick: false, // Désactiver le zoom automatique, on le gère manuellement dans clusterclick
        spiderfyOnMaxZoom: false, // Spider uniquement si pins superposés (voir clusterclick)
        showCoverageOnHover: false,
        animate: true, // Activer les animations
        disableClusteringAtZoom: 18, // Désactiver le clustering au zoom 18 pour toujours voir les pins individuels
        chunkedLoading: true,
        // Lignes du spider : couleur primary du widget (défaut plugin : #222)
        spiderLegPolylineOptions: {
          weight: 1.5,
          color: primaryColor,
          opacity: 0.5,
        },
      });

      // Masquer les lignes spiderfy si elles apparaissent malgré tout
      // Afficher le bouton "Rechercher dans cette zone" au lieu de faire l'appel automatique
      // Seulement si on a un widgetId (le bouton est affiché même en mode composition)
      if (this.widgetId) {
        this.map.on("zoomend", () => {
          if (!this.map || !this.mapInitialized) return;
          // Fermer tous les popups ouverts lors du zoom (pour éviter qu'un pin sélectionné reste affiché s'il entre dans un cluster)
          this.map.closePopup();
          // Après la fin de l'animation du plugin (sinon spiderfy() refuse de s'exécuter), spiderfy les groupes de pins superposés
          setTimeout(
            () => this._spiderfyOverlappingOnZoomEnd(),
            MapWidget.SPIDERFY_AFTER_ZOOM_DELAY_MS,
          );
          // Calculer les paramètres de recherche et afficher le bouton
          this.prepareSearchButton();
        });

        // Écouter les déplacements de la carte pour afficher le bouton de recherche
        this.map.on("moveend", () => {
          if (!this.map || !this.mapInitialized) return;
          // Calculer les paramètres de recherche et afficher le bouton
          this.prepareSearchButton();
        });
      } else {
        // En mode composition (disableDetailView) ou sans widgetId
        this.map.on("zoomend", () => {
          if (!this.map || !this.mapInitialized) return;
          // Fermer tous les popups ouverts lors du zoom
          this.map.closePopup();
          // Après la fin de l'animation du plugin (sinon spiderfy() refuse de s'exécuter), spiderfy les groupes de pins superposés
          setTimeout(
            () => this._spiderfyOverlappingOnZoomEnd(),
            MapWidget.SPIDERFY_AFTER_ZOOM_DELAY_MS,
          );
        });
      }

      // Clic sur un cluster : toujours zoomer (jamais spiderfy au clic).
      // Le spider n'apparaît que via zoomend quand zoom >= disableClusteringAtZoom (plus de cluster visible).
      this.markerClusterGroup.on("clusterclick", (e: any) => {
        const cluster = e.layer;
        const group = this.markerClusterGroup as any;
        // Si ce cluster est déjà spiderfié (spider affiché au zoom 18+) : empêcher le clic de remonter à la carte (unspiderfy)
        if (group._spiderfied === cluster) {
          if (e.originalEvent) {
            e.originalEvent.stopPropagation();
            e.originalEvent.preventDefault();
          }
          return;
        }
        const markers = cluster.getAllChildMarkers();
        if (markers.length === 0 || !this.map) return;

        // Toujours zoomer sur le cluster
        const boundsGroup = new L.FeatureGroup(markers);
        const bounds = boundsGroup.getBounds();
        const currentZoom = this.map.getZoom();
        const newZoom = Math.min(this.map.getBoundsZoom(bounds) + 1, 18);

        if (newZoom > currentZoom) {
          this.map.flyToBounds(bounds, {
            padding: [50, 50],
            maxZoom: 18,
            duration: 0.8,
            easeLinearity: 0.25,
          });
        } else {
          this.map.flyTo(this.map.getCenter(), 18, {
            duration: 0.8,
            easeLinearity: 0.25,
          });
        }
      });

      // Quand on unspiderfy au zoom >= disableClusteringAtZoom, le plugin retire les marqueurs de la carte
      // sans les réafficher (pas de cluster à ce zoom) : on les réajoute pour que les pins restent visibles.
      this.markerClusterGroup.on("unspiderfied", (e: any) => {
        if (!this.map || this.map.getZoom() < 18) return;
        const fg = (this.markerClusterGroup as any)._featureGroup;
        const markers = e.markers || [];
        markers.forEach((m: L.Marker) => {
          if (m && !fg.hasLayer(m)) {
            fg.addLayer(m);
          }
        });
      });

      // Créer les marqueurs et calculer les bounds
      const bounds: L.LatLngBoundsExpression = [];

      itemsWithCoords.forEach((item) => {
        const coords = item.localisation?.geolocalisation?.geoJson?.coordinates;
        if (coords && coords.length >= 2) {
          const [lng, lat] = coords;
          const latLng: L.LatLngExpression = [lat, lng];
          bounds.push(latLng);

          // Créer le marqueur avec l'icône personnalisée
          const customIcon = this.createCustomMarkerIcon();
          const marker = L.marker(latLng, { icon: customIcon });
          this.markers.push(marker);

          // Stocker la relation item -> marker
          this.itemMarkerMap.set(item, marker);

          // Créer le contenu du popup
          const popupContent = this.createPopupContent(item);
          marker.bindPopup(popupContent, {
            maxWidth: 300,
            className: "custom-popup",
          });

          // Écouter l'ouverture du popup pour changer la couleur du marqueur
          marker.on("popupopen", () => {
            this.updateMarkerIcon(marker, "#2F2F2F");
          });

          // Écouter la fermeture du popup pour remettre la couleur par défaut ou la couleur de sélection
          marker.on("popupclose", () => {
            // Si c'est l'item sélectionné, garder la couleur de sélection (gris foncé)
            if (this.selectedItemId === item.id) {
              this.updateMarkerIcon(marker, "#2F2F2F");
            } else {
              // Utiliser la couleur primary de la config
              const primaryColor =
                this.config?.visualCustomization?.colors?.primary || "#168ac8";
              this.updateMarkerIcon(marker, primaryColor);
            }

            // Si on est en mode composition (disableDetailView), désélectionner dans la liste aussi (sauf pendant updateMarkers)
            if (
              !this._isUpdatingMarkers &&
              this.disableDetailView &&
              this.selectedItemId === item.id
            ) {
              this.selectedItemId = null;
              this.dispatchEvent(
                new CustomEvent("item-deselect", {
                  detail: { itemId: item.id },
                  bubbles: true,
                  composed: true,
                }),
              );
            }
          });

          // Si c'est l'item sélectionné, mettre en évidence le marqueur (gris foncé)
          if (this.selectedItemId === item.id) {
            this.updateMarkerIcon(marker, "#2F2F2F");
          } else {
            // Sinon, couleur primary de la config
            const primaryColor =
              this.config?.visualCustomization?.colors?.primary || "#168ac8";
            this.updateMarkerIcon(marker, primaryColor);
          }

          // Ajouter le marqueur au cluster au lieu de directement à la carte
          this.markerClusterGroup!.addLayer(marker);
        }
      });

      // Ajouter le cluster group à la carte (même s'il est vide)
      this.markerClusterGroup.addTo(this.map);

      // Quand un spider est affiché : distinguer vrai clic (désélection) et pan (déplacement) pour ne pas fermer le popup au pan.
      const mapContainer = this.map.getContainer();
      this._mapMouseDownCaptureHandler = (e: MouseEvent) => {
        const group = this.markerClusterGroup as any;
        if (!group?._spiderfied) return;
        const target = e.target as HTMLElement;
        if (
          target.closest?.(".leaflet-marker-icon") ||
          target.closest?.(".leaflet-popup")
        )
          return;
        this._mapMouseDownAt = { x: e.clientX, y: e.clientY };
      };
      mapContainer.addEventListener(
        "mousedown",
        this._mapMouseDownCaptureHandler,
        true,
      );
      this._mapClickCaptureHandler = (e: MouseEvent) => {
        const group = this.markerClusterGroup as any;
        if (!group?._spiderfied) return;
        const target = e.target as HTMLElement;
        if (
          target.closest?.(".leaflet-marker-icon") ||
          target.closest?.(".leaflet-popup")
        )
          return;
        const at = this._mapMouseDownAt;
        this._mapMouseDownAt = null;
        const moved = at
          ? Math.hypot(e.clientX - at.x, e.clientY - at.y) > 5
          : false;
        if (!moved) this.map?.closePopup();
        e.stopPropagation();
        e.preventDefault();
      };
      mapContainer.addEventListener(
        "click",
        this._mapClickCaptureHandler,
        true,
      );

      // Forcer le recalcul de la taille et ajuster la vue
      // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (this.map) {
            // Invalider plusieurs fois pour s'assurer que la taille est correcte
            this.map.invalidateSize();
            requestAnimationFrame(() => {
              if (this.map) {
                this.map.invalidateSize();
                const defaultZoom =
                  (this.config?.templateSpecificConfig as any)?.config
                    ?.defaultZoom || 15;
                if (bounds.length > 0) {
                  // Si on a des items, ajuster la vue sur eux
                  this.map.fitBounds(bounds, {
                    padding: [50, 50],
                    maxZoom: defaultZoom,
                  });
                } else {
                  // Si pas d'items, centrer sur la France par défaut (centre de la France, zoom 6)
                  this.map.setView([46.6034, 1.8883], 6);
                }
                // Invalider une dernière fois après le fitBounds/setView
                requestAnimationFrame(() => {
                  if (this.map) {
                    this.map.invalidateSize();
                    // Marquer la carte comme initialisée après le dernier invalidateSize
                    this.mapInitialized = true;
                    // Si un item était déjà sélectionné (ex: liste cliquée puis carte dépliée), sélectionner le pin et centrer
                    this.updateMarkerSelection();
                    if (this.selectedItemId) {
                      setTimeout(
                        () => this.selectMarker(this.selectedItemId!),
                        150,
                      );
                    }
                  }
                });
              }
            });
          }
        }, 100);
      });
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la carte:", error);
      this.error = "Impossible d'initialiser la carte";
    }
  }

  /** Seuil en mètres : pins à moins de cette distance = superposés → spiderfy au lieu de zoomer */
  private static readonly SPIDERFY_DISTANCE_THRESHOLD_METERS = 15;

  /**
   * Retourne true si les marqueurs doivent être spiderfiés (superposés ou bounds quasi nulles).
   */
  private _shouldSpiderfyCluster(
    markers: L.Marker[],
    distanceThresholdMeters: number,
  ): boolean {
    if (markers.length < 2) return false;
    const firstLatLng = markers[0].getLatLng();
    const allOverlapping = markers.every(
      (m) => firstLatLng.distanceTo(m.getLatLng()) < distanceThresholdMeters,
    );
    if (allOverlapping) return true;
    const group = new L.FeatureGroup(markers);
    const bounds = group.getBounds();
    const diagonalMeters = bounds
      .getNorthWest()
      .distanceTo(bounds.getSouthEast());
    return diagonalMeters < distanceThresholdMeters;
  }

  /**
   * Au zoomend, si on est au niveau où les clusters éclatent (zoom >= disableClusteringAtZoom),
   * spiderfy les groupes de pins superposés pour qu’ils restent tous visibles.
   */
  /** Délai (ms) après zoomend avant spiderfy : le plugin refuse spiderfy() tant que _inZoomAnimation est vrai. */
  private static readonly SPIDERFY_AFTER_ZOOM_DELAY_MS = 350;

  private _spiderfyOverlappingOnZoomEnd(): void {
    if (!this.map || !this.markerClusterGroup) return;
    const zoom = this.map.getZoom();
    if (zoom < 18) return; // 18 = disableClusteringAtZoom
    const threshold = MapWidget.SPIDERFY_DISTANCE_THRESHOLD_METERS;
    const seenClusters = new Set<unknown>();
    this.markerClusterGroup.eachLayer((layer: any) => {
      const cluster = layer.__parent;
      if (!cluster || seenClusters.has(cluster)) return;
      if (
        typeof cluster.getChildCount !== "function" ||
        cluster.getChildCount() < 2
      )
        return;
      seenClusters.add(cluster);
      const markers = cluster.getAllChildMarkers();
      if (this._shouldSpiderfyCluster(markers, threshold)) {
        cluster.spiderfy();
      }
    });
  }

  /**
   * Crée une icône de cluster personnalisée
   */
  private createClusterIcon(count: number, primaryColor: string): L.DivIcon {
    const svg = `
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <!-- Cercle extérieur avec opacité 50% -->
        <circle cx="20" cy="20" r="20" fill="${primaryColor}" opacity="0.5"/>
        <!-- Cercle intérieur opaque -->
        <circle cx="20" cy="20" r="16" fill="${primaryColor}" opacity="1"/>
        <!-- Texte du nombre -->
        <text x="20" y="20" text-anchor="middle" dominant-baseline="central" 
              fill="white" font-family="Inter, sans-serif" font-weight="400" 
              font-size="14px" style="user-select: none;">${count}</text>
      </svg>
    `;

    const encodedSvg = encodeURIComponent(svg);
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

    return L.divIcon({
      html: `<img src="${dataUrl}" alt="cluster" style="width: 40px; height: 40px; display: block;" />`,
      className: "custom-marker-cluster",
      iconSize: L.point(40, 40),
      iconAnchor: L.point(20, 20),
    });
  }

  /**
   * Crée une icône personnalisée pour les marqueurs de carte
   * Design: rond blanc de 28px avec bordure et pointe arrondie vers le bas (total 28x32px),
   * contenant un rond de couleur primary de 20px (ou #2F2F2F si sélectionné)
   */
  private createCustomMarkerIcon(color?: string): L.DivIcon {
    const defaultColor =
      this.config?.visualCustomization?.colors?.primary || "#168ac8";
    const circleColor = color || defaultColor;

    // Créer un SVG pour le marqueur personnalisé
    // Design basé sur Rectangle 4.svg: cercle parfait qui se transforme en pointe arrondie
    // Cercle de 28px (rayon 14) centré en (14, 14), qui se prolonge en pointe vers le bas
    // Total: 28px de large, 32px de haut
    // À l'intérieur: rond couleur primary 20px (rayon 10)
    const svg = `
      <svg width="28" height="32" viewBox="0 0 28 32" xmlns="http://www.w3.org/2000/svg">
        <!-- Forme principale: cercle qui se transforme en pointe (basé sur Rectangle 4.svg) -->
        <!-- Le cercle est centré en (14, 14) avec rayon 14, puis les côtés descendent en pointe arrondie -->
        <!-- Adaptation pour 32px de hauteur au lieu de 31px -->
        <path d="M 0 14 C 0 6.26801 6.26801 0 14 0 C 21.732 0 28 6.26801 28 14 V 14.4025 
                C 28 18.9772 25.8267 23.2799 22.1447 25.9947 L 17.1636 30.6674 
                C 15.2826 32.0543 12.7174 32.0543 10.8364 30.6674 L 5.85532 25.9947 
                C 2.1733 23.2799 0 18.9772 0 14.4025 V 14 Z"
              fill="white" 
              stroke="#AAB4C380" 
              stroke-width="1"/>
        <!-- Cercle intérieur de couleur: 20px de diamètre (rayon 10), centré en (14, 14) -->
        <circle cx="14" cy="14" r="10" fill="${circleColor}"/>
      </svg>
    `;

    // Convertir le SVG en data URL
    const encodedSvg = encodeURIComponent(svg);
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

    return L.divIcon({
      html: `<img src="${dataUrl}" alt="marker" style="width: 28px; height: 32px; display: block;" />`,
      className: "custom-marker-icon",
      iconSize: [28, 32],
      iconAnchor: [14, 32], // Point d'ancrage au centre horizontal, en bas (pointe)
      popupAnchor: [0, -32],
    });
  }

  /**
   * Met à jour l'icône d'un marqueur avec une nouvelle couleur
   */
  private updateMarkerIcon(marker: L.Marker, color?: string): void {
    const newIcon = this.createCustomMarkerIcon(color);
    marker.setIcon(newIcon);
  }

  /**
   * Recentre la carte sur le marqueur quand un popup s'ouvre, pour éviter que la card
   * soit masquée sous le bouton "Rechercher dans cette zone" (partie haute de l'écran).
   */
  private ensureMarkerVisibleWithPopup(marker: L.Marker): void {
    if (!this.map) return;
    const latLng = marker.getLatLng();
    const size = this.map.getSize();
    if (!size) return;
    const point = this.map.latLngToContainerPoint(latLng);
    // Si le marqueur est dans la partie haute (ex. top 35 %), recentrer pour mettre
    // le marqueur vers 55 % de la hauteur afin que la popup reste visible sous le bouton
    const topThreshold = size.y * 0.35;
    if (point.y < topThreshold) {
      const targetY = size.y * 0.55;
      const offset: [number, number] = [
        point.x - size.x / 2,
        point.y - targetY,
      ];
      this.map.panBy(offset, { animate: true, duration: 0.25 });
    }
  }

  /**
   * Met à jour la sélection des marqueurs en fonction de selectedItemId
   */
  private updateMarkerSelection(): void {
    if (!this.map || !this.markerClusterGroup) return;

    // Parcourir tous les items et mettre à jour leurs marqueurs
    this.itemMarkerMap.forEach((marker, item) => {
      // Vérifier si le marqueur est visible (pas dans un cluster ou cluster ouvert)
      const parentCluster = (marker as any).__parent;
      const isVisible =
        !parentCluster ||
        (parentCluster._zoom !== undefined &&
          parentCluster._zoom <= this.map!.getZoom());

      if (this.selectedItemId === item.id) {
        // Marqueur sélectionné : couleur grise foncée (#2F2F2F)
        if (isVisible) {
          this.updateMarkerIcon(marker, "#2F2F2F");
        }
      } else {
        // Marqueur non sélectionné : couleur primary de la config
        // (sauf si un popup est ouvert, ce qui est géré par les événements popup)
        if (!marker.isPopupOpen() && isVisible) {
          const primaryColor =
            this.config?.visualCustomization?.colors?.primary || "#168ac8";
          this.updateMarkerIcon(marker, primaryColor);
        }
      }
    });

    // Si le marqueur sélectionné est dans un cluster, essayer de le récupérer après un court délai
    if (this.selectedItemId) {
      setTimeout(() => {
        this.itemMarkerMap.forEach((marker, item) => {
          if (this.selectedItemId === item.id) {
            const parentCluster = (marker as any).__parent;
            // Si le marqueur n'est plus dans un cluster, mettre à jour sa couleur (gris foncé pour sélectionné)
            if (
              !parentCluster ||
              (parentCluster._zoom !== undefined &&
                parentCluster._zoom <= this.map!.getZoom())
            ) {
              this.updateMarkerIcon(marker, "#2F2F2F");
            }
          }
        });
      }, 300);
    }
  }

  /**
   * Sélectionne un marqueur par son ID d'item (méthode publique pour être appelée depuis le parent)
   * Déclusterise si nécessaire et centre la carte sur le marqueur
   */
  public selectMarker(itemId: number): void {
    this.selectedItemId = itemId;

    // Trouver l'item et son marqueur
    let targetMarker: L.Marker | null = null;
    let targetItem: ObjetTouristique | null = null;

    for (const [item, marker] of this.itemMarkerMap.entries()) {
      if (item.id === itemId) {
        targetMarker = marker;
        targetItem = item;
        break;
      }
    }

    if (!targetMarker || !targetItem || !this.map) {
      // Mettre à jour la sélection visuelle même si le marqueur n'est pas trouvé
      this.updateMarkerSelection();
      return;
    }

    const itemLocalisation = targetItem.localisation;
    if (itemLocalisation) {
      const coords = itemLocalisation.geolocalisation?.geoJson?.coordinates;
      if (!coords || coords.length < 2) {
        return;
      }

      const [lng, lat] = coords;

      // Vérifier si le marqueur est dans un cluster
      const parentCluster = (targetMarker as any).__parent;

      if (parentCluster && this.markerClusterGroup) {
        // Le marqueur est dans un cluster, il faut zoomer pour le déclusteriser
        // Obtenir tous les marqueurs du cluster pour calculer les bounds
        const clusterMarkers = parentCluster.getAllChildMarkers();
        if (clusterMarkers.length > 0) {
          // Créer un groupe avec tous les marqueurs du cluster
          const group = new L.FeatureGroup(clusterMarkers);
          const bounds = group.getBounds();

          // Calculer le zoom nécessaire pour voir tous les pins
          const currentZoom = this.map.getZoom();
          const newZoom = Math.min(this.map.getBoundsZoom(bounds) + 1, 18);

          // Si on est déjà assez proche, zoomer au maximum pour déclusteriser
          if (newZoom > currentZoom) {
            // Zoomer avec transition fluide
            this.map.flyToBounds(bounds, {
              padding: [50, 50],
              maxZoom: 18,
              duration: 0.8,
              easeLinearity: 0.25,
            });
          } else {
            // Si on ne peut pas zoomer davantage, forcer le zoom max pour déclusteriser
            this.map.flyTo([lat, lng], 18, {
              duration: 0.8,
              easeLinearity: 0.25,
            });
          }
        } else {
          // Fallback : zoomer directement sur le marqueur
          this.map.flyTo([lat, lng], 18, {
            duration: 0.8,
            easeLinearity: 0.25,
          });
        }

        // Attendre que le zoom soit terminé pour mettre à jour la sélection et ouvrir la popup
        this.map.once("zoomend", () => {
          // Attendre un peu plus pour que le clustering soit terminé
          setTimeout(() => {
            this.updateMarkerSelection();
            // Récupérer le marqueur à nouveau au cas où il aurait changé après le zoom
            let finalMarker: L.Marker | null = null;
            for (const [it, m] of this.itemMarkerMap.entries()) {
              if (it.id === itemId) {
                finalMarker = m;
                break;
              }
            }
            // Ouvrir la popup du marqueur sélectionné
            if (finalMarker) {
              // Attendre encore un peu pour que le marqueur soit bien visible
              setTimeout(() => {
                finalMarker!.openPopup();
              }, 100);
            }
          }, 300);
        });
        return;
      } else {
        // Le marqueur n'est pas dans un cluster, centrer la carte dessus
        this.map.flyTo([lat, lng], Math.max(this.map.getZoom(), 16), {
          duration: 0.5,
        });
        // Mettre à jour immédiatement la sélection et ouvrir la popup
        setTimeout(() => {
          this.updateMarkerSelection();
          // Récupérer le marqueur à nouveau au cas où il aurait changé
          let finalMarker: L.Marker | null = null;
          for (const [it, m] of this.itemMarkerMap.entries()) {
            if (it.id === itemId) {
              finalMarker = m;
              break;
            }
          }
          // Ouvrir la popup du marqueur sélectionné
          if (finalMarker) {
            setTimeout(() => {
              finalMarker!.openPopup();
            }, 150);
          }
        }, 100);
      }
    } else {
      // Mettre à jour la sélection visuelle même si le marqueur n'est pas trouvé
      this.updateMarkerSelection();
    }
  }

  private createPopupContent(item: ObjetTouristique): HTMLElement {
    const div = document.createElement("div");
    div.className = "popup-card";

    const imageUrl = getImageUrl(item, "liste", this.language);
    const nom = getLabel(item.nom, this.language);
    const showImage = !!imageUrl && !this.failedPopupImages.has(item.id);
    // Cacher le lien uniquement quand la carte est dans la fiche détail (isChildWidget sans disableDetailView).
    const showDetailLink = !this.isChildWidget || this.disableDetailView;

    div.innerHTML = `
      ${showImage ? `<img class="popup-image" src="${imageUrl}" alt="${nom}" />` : ""}
      <div class="popup-content">
        <h4 class="popup-title">${nom}</h4>
        ${showDetailLink ? `<a class="popup-link" href="#" style="color: #2F2F2F !important; text-decoration: underline;">${t("viewDetail", this.language) || "Voir le détail"}</a>` : ""}
      </div>
    `;

    if (showImage) {
      const img = div.querySelector(".popup-image") as HTMLImageElement;
      if (img) {
        img.addEventListener("error", () => {
          this.failedPopupImages = new Set(this.failedPopupImages).add(item.id);
          img.style.display = "none";
        });
      }
    }

    // Pas d'action au clic uniquement quand carte dans fiche détail (isChildWidget sans disableDetailView)
    if (showDetailLink) {
      // Gérer le clic sur le popup et le lien
      div.addEventListener("click", (e) => {
        e.stopPropagation();
        if (this.disableDetailView) {
          this.dispatchEvent(
            new CustomEvent("item-click", {
              detail: { item },
              bubbles: true,
              composed: true,
            }),
          );
        } else {
          this.handleItemClick(item);
        }
      });

      const link = div.querySelector(".popup-link");
      if (link) {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.disableDetailView) {
            this.dispatchEvent(
              new CustomEvent("item-detail-click", {
                detail: { item },
                bubbles: true,
                composed: true,
              }),
            );
          } else {
            this.handleItemClick(item);
          }
        });
      }
    }

    return div;
  }

  protected renderWidget() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          <p>${t("loading", this.language)}</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderErrorScreen();
    }

    const hasFilterFields =
      this.config?.contentCustomization?.filterFields &&
      this.config.contentCustomization.filterFields.length > 0;
    const showFiltersConfig =
      hasFilterFields &&
      (this.config?.templateSpecificConfig as any)?.config?.showFilters !==
        false;
    const showFilters = showFiltersConfig && this.filtersVisible;
    const layoutFraction = this.getLayoutFraction();

    const itemsWithCoords = this.items.filter(
      (item) => item.localisation?.geolocalisation?.geoJson?.coordinates,
    );

    const visual = this.config?.visualCustomization ?? null;
    return html`
      <apidae-widget-layout
        .config=${{
          fraction: this.hideHeaderAndFilters ? "full" : layoutFraction,
          showFilters: this.hideHeaderAndFilters ? false : showFilters,
          headerSlot: this.hideHeaderAndFilters ? undefined : "header",
          filtersSlot: this.hideHeaderAndFilters
            ? undefined
            : showFilters
              ? "filters"
              : undefined,
          mapSlot: "map",
        }}
        .visualCustomization=${visual}
      >
        ${!this.hideHeaderAndFilters
          ? html`
              <div slot="header">
                <apidae-widget-header
                  .config=${{
                    widgetName: this.config?.name,
                    logo: this.config?.visualCustomization?.logo,
                    logoUrl: this.config?.visualCustomization?.logoUrl,
                    language: this.language,
                    showFiltersButton: showFiltersConfig,
                    showClearButton: showFiltersConfig,
                    hasActiveFilters: this.hasActiveFilters(),
                    showSwitcher: false,
                    filtersVisible: this.filtersVisible,
                    resultsCount: this.getResultsCountForDisplay(),
                    primaryColor:
                      this.config?.visualCustomization?.colors?.primary,
                    secondaryColor:
                      this.config?.visualCustomization?.colors?.secondary,
                  }}
                  .visualCustomization=${visual}
                  @toggle-filters=${this.handleToggleFilters}
                  @clear-filters=${this.handleClearFilters}
                ></apidae-widget-header>
              </div>

              ${showFilters
                ? html`
                    <div slot="filters" class="filters-slot-wrapper">
                      <apidae-filter-panel
                        .filterGroups=${this.filterGroups}
                        .language=${this.language}
                        .activeFilters=${this.activeFilters}
                        .dateRange=${this.dateRange}
                        .loadingMoreFacetId=${this.loadingMoreFacetId}
                        .visualCustomization=${visual}
                        @filter-change=${this.handleFilterChange}
                        @load-more-facet=${this.handleLoadMoreFacet}
                      ></apidae-filter-panel>
                    </div>
                  `
                : ""}
            `
          : ""}

        <div slot="map" class="map-main">
          ${this.items.length === 0 &&
          !this.mapInitialized &&
          !this.hideEmptyState
            ? html`
                <div class="empty-state">
                  <div class="empty-state-title">
                    ${t("noResultsFound", this.language) ||
                    "Aucun résultat trouvé."}
                  </div>
                  <div class="empty-state-subtitle">
                    ${t("adjustSearchWithFilters", this.language) ||
                    "Ajustez votre recherche grâce aux filtres."}
                  </div>
                </div>
              `
            : html`
                <div class="map-container">
                  <div id="${this.mapId}" class="leaflet-map"></div>
                  ${this.showSearchButton && this.widgetId
                    ? html`
                        <button
                          class="search-area-button"
                          @click=${() => this.loadDataFromMapBounds()}
                        >
                          <apidae-icon
                            name="bold/search/magnifer"
                            size="20"
                            color="#2F2F2F"
                            .visualCustomization=${visual}
                          ></apidae-icon>
                          ${t("searchInThisArea", this.language) ||
                          "Rechercher dans cette zone"}
                        </button>
                      `
                    : ""}
                  ${itemsWithCoords.length > 0
                    ? html`
                        <div class="map-overlay">
                          ${itemsWithCoords.length}
                          ${itemsWithCoords.length > 1 ? "lieux" : "lieu"}
                        </div>
                      `
                    : html``}
                </div>
              `}
        </div>
      </apidae-widget-layout>
    `;
  }

  render() {
    return html`
      <div class="widget-container">
        <div class="widget-content">${this.renderWidget()}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-carte": MapWidget;
  }
}
